"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { X, ChevronDown } from "lucide-react";
import {
  Trash2,
  BicepsFlexed,
  Truck,
  Warehouse,
  Armchair,
  WashingMachine,
  Monitor,
  Recycle,
  ShoppingBag,
  Hammer,
  Send,
  Wrench,
} from "lucide-react";
import Image from "next/image";
import MobileNav from "./MobileNav";
import LanguageSelector from "./LanguageSelector";
// import TopBanner, { BANNER_HEIGHT } from "./TopBanner";

import type { Locale } from "./templates/ServicePageTemplate";
import { isHrefHidden } from "@/lib/booking/hidden-services";

const allServicesSv = [
  { name: "Bortforsling", icon: Trash2, color: "#f89544", href: "/bortforsling" },
  { name: "Bärhjälp", icon: BicepsFlexed, color: "#22c55e", href: "/barhjalp" },
  { name: "Flytthjälp", icon: Truck, color: "#8b5cf6", href: "/flytthjalp" },
  { name: "Transporthjälp", icon: Monitor, color: "#6366f1", href: "/transporthjalp" },
  { name: "Transport möbler", icon: Armchair, color: "#e11d48", href: "/transport-mobler" },
  { name: "Transport vitvaror", icon: WashingMachine, color: "#f59e0b", href: "/transport-vitvaror" },
  { name: "Transport förråd", icon: Warehouse, color: "#0d9488", href: "/transport-forrad" },
  { name: "Transport återbruk", icon: Recycle, color: "#16a34a", href: "/transport-aterbruk" },
  { name: "Hemleverans butik", icon: ShoppingBag, color: "#ec4899", href: "/hemleverans-butik" },
  { name: "Hemleverans auktion", icon: Hammer, color: "#ef4444", href: "/hemleverans-auktion" },
  { name: "Möbelmontering", icon: Wrench, color: "#3b82f6", href: "/mobelmontering" },
  { name: "Skicka bud", icon: Send, color: "#0ea5e9", href: "/bud" },
];

const allServicesEn = [
  { name: "Junk Removal", icon: Trash2, color: "#f89544", href: "/en/junk-removal" },
  { name: "Carrying Help", icon: BicepsFlexed, color: "#22c55e", href: "/en/carrying-help" },
  { name: "Moving Help", icon: Truck, color: "#8b5cf6", href: "/en/moving-help" },
  { name: "Transport Help", icon: Monitor, color: "#6366f1", href: "/en/transport-help" },
  { name: "Furniture Transport", icon: Armchair, color: "#e11d48", href: "/en/furniture-transport" },
  { name: "Appliance Transport", icon: WashingMachine, color: "#f59e0b", href: "/en/appliance-transport" },
  { name: "Storage Transport", icon: Warehouse, color: "#0d9488", href: "/en/storage-transport" },
  { name: "Recycling Transport", icon: Recycle, color: "#16a34a", href: "/en/second-hand-transport" },
  { name: "Store Delivery", icon: ShoppingBag, color: "#ec4899", href: "/en/store-delivery" },
  { name: "Auction Delivery", icon: Hammer, color: "#ef4444", href: "/en/auction-delivery" },
  { name: "Furniture Assembly", icon: Wrench, color: "#3b82f6", href: "/en/furniture-assembly" },
  { name: "Send Courier", icon: Send, color: "#0ea5e9", href: "/en/courier-service" },
];

// Pausade tjänster (lib/booking/hidden-services.ts) visas inte.
const servicesSv = allServicesSv.filter((s) => !isHrefHidden(s.href));
const servicesEn = allServicesEn.filter((s) => !isHrefHidden(s.href));

export const services = servicesSv;

export function getServices(locale: Locale) {
  return locale === "en" ? servicesEn : servicesSv;
}

const t = {
  sv: { services: "Tjänster", seePrice: "Se pris", business: "Företag", phone: "08-517 082 81", book: "Boka", menu: "Meny" },
  en: { services: "Services", seePrice: "See price", business: "Business", phone: "08-517 082 81", book: "Book", menu: "Menu" },
};

function ServiceItem({ service: s }: { service: (typeof services)[number] }) {
  const [hovered, setHovered] = useState(false);
  const Icon = s.icon;
  return (
    <a
      key={s.name}
      href={s.href}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="flex items-center gap-3.5 rounded-xl px-3 py-2.5 text-[15px] transition-colors"
      style={{
        backgroundColor: hovered ? s.color + "12" : "transparent",
        color: hovered ? s.color : "#374151",
      }}
    >
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
        style={{ backgroundColor: s.color + "14" }}
      >
        <Icon
          className="h-[18px] w-[18px]"
          style={{ color: s.color }}
          strokeWidth={2}
        />
      </span>
      <span className="font-medium">{s.name}</span>
    </a>
  );
}

export default function Header({
  locale = "sv",
  bookingHref,
}: {
  locale?: Locale;
  bookingHref?: string;
}) {
  const localServices = getServices(locale);
  const l = t[locale];
  const href = bookingHref ?? (locale === "en" ? "/en/book" : "/boka");
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const openDropdown = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setDropdownOpen(true);
  };

  const closeDropdown = () => {
    timeoutRef.current = setTimeout(() => setDropdownOpen(false), 150);
  };

  return (
    <>
      {/* <TopBanner /> */}
      <header
        className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
          mobileOpen
            ? "!bg-transparent shadow-none"
            : scrolled
              ? "bg-white/95 backdrop-blur-md shadow-sm"
              : "bg-white"
        }`}
        style={{ top: 0 }}
      >
        <div className="mx-auto max-w-screen-2xl flex items-center justify-between px-6 py-4 lg:py-5 xl:px-14">
          {/* Left: Logo */}
          <div className="flex items-center">
            <Link href="/" className="flex items-center">
              <Image
                src="/uk-logo.png"
                alt="Ungdomskraft"
                width={500}
                height={80}
                className="h-7 sm:h-8 w-auto object-contain"
                priority
              />
            </Link>
          </div>

          {/* Center: Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            <div
              className="relative"
              onMouseEnter={openDropdown}
              onMouseLeave={closeDropdown}
            >
              <button
                className={`flex items-center gap-1.5 text-[15px] font-semibold transition-colors ${
                  dropdownOpen
                    ? "text-brand-secondary"
                    : "text-gray-800 hover:text-brand-secondary"
                }`}
              >
                {l.services}
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-200 ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {dropdownOpen && (
                <div
                  className="absolute top-full left-0 mt-4 w-[720px] rounded-2xl bg-white shadow-xl ring-1 ring-black/[0.06] p-5 animate-[hero-fade-up_0.18s_ease-out_both]"
                  onMouseEnter={openDropdown}
                  onMouseLeave={closeDropdown}
                >
                  <div className="grid grid-cols-3 gap-x-4 gap-y-1">
                    {localServices.map((s) => (
                      <ServiceItem key={s.name} service={s} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href={href}
              className="text-[15px] font-semibold text-gray-800 hover:text-brand-secondary transition-colors"
            >
              {l.seePrice}
            </a>
            <a
              href="tel:+46851708281"
              className="text-[15px] font-semibold text-gray-800 hover:text-brand-secondary transition-colors border-2 border-brand-secondary rounded-xl px-4 py-1.5"
            >
              {l.phone}
            </a>
          </nav>

          {/* Right: Language + CTA + Mobile hamburger */}
          <div className="flex items-center gap-3">
            <div className="hidden lg:block">
              <LanguageSelector locale={locale} />
            </div>
            <a
              href={href}
              className="inline-flex items-center justify-center rounded-xl bg-brand-secondary px-5 py-2 text-[14px] lg:px-6 lg:py-2.5 lg:text-[15px] font-bold text-white hover:brightness-110 transition-all"
            >
              {l.book}
            </a>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden flex h-10 w-10 -mr-2.5 items-center justify-center rounded-lg text-gray-800 hover:bg-gray-100 transition-colors"
              aria-label={l.menu}
            >
              {mobileOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <div className="flex flex-col items-end gap-[5px]">
                  <span className="block h-[2px] w-5 rounded-full bg-gray-800" />
                  <span className="block h-[2px] w-4 rounded-full bg-gray-800" />
                </div>
              )}
            </button>
          </div>
        </div>
      </header>

      <MobileNav
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        locale={locale}
        bookingHref={href}
      />
    </>
  );
}
